import React, { useContext, useState, useEffect, useCallback } from "react";
import {
  Card,
  Spinner,
  Button,
  Modal,
  Breadcrumb,
  Tabs,
  TextInput,
  Select,
  Tooltip,
  Label,
} from "flowbite-react";
import { Store } from "../context/store";
import { IoMdAddCircleOutline, IoMdArrowBack } from "react-icons/io";
import { useNavigate, Link, useParams } from "react-router-dom";
import apiRequest from "../utils/apiRequest";
import {
  HiCloudUpload,
  HiOutlineCheck,
  HiOutlineExclamationCircle,
  HiPencil,
} from "react-icons/hi";
import img from "../assets/images/user.png";
import { formatDate } from "../utils/date";
import { useAllPermissions } from "../utils/usePermission";
import { confirmDialog } from "../components/ConfirmDialog";
import DashboardLayout from "../components/layout/DashboardLayout";

const isInactiveStaff = (staffMember) =>
  staffMember?.status?.toLowerCase?.() === "inactive";

const hideInactiveSupervisor = (subscription) => {
  if (!isInactiveStaff(subscription?.supervisor)) {
    return subscription;
  }

  return {
    ...subscription,
    supervisor: null,
  };
};

function SingleCustomer() {
  let store = useContext(Store);
  let [baseUrl] = store.url;
  const [loggedInUser] = store.loggedInUser;

  let [msg, setMsg] = useState([]);
  let [subs, setSubs] = useState([]);
  let [loading, setLoading] = useState(false);
  let [client, setClient] = useState({});
  const [openModalOut, setOpenModalOut] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openModalImage, setOpenModalImage] = useState(false);
  const [openEditProfileModal, setOpenEditProfileModal] = useState(false);
  let [imageOrId, setImageOrId] = useState("");
  let [error, setError] = useState("");
  let [errorColor, setErrorColor] = useState("");
  let [loading3, setLoading3] = useState(false);
  const [loadingProfileUpdate, setLoadingProfileUpdate] = useState(false);
  const [profileError, setProfileError] = useState("");
  let [properties, setProperties] = useState([]);
  let [staff, setStaff] = useState([]);
  let [files, setFiles] = useState([]);

  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [currentSubId, setCurrentSubId] = useState(null); // ID of the subscription being updated
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [updateAction, setUpdateAction] = useState(""); // property-unit | payment

  const [updateSubscription, setUpdateSubscription] = useState({
    propertyId: "",
    unitId: "",
    amount: null,
    supervisor: "",
  });

  const [paymentData, setPaymentData] = useState({
    amountPaid: "",
    proof: "",
  });

  const { id } = useParams();
  const [subscription, setSubscription] = useState({
    propertyId: "",
    unitId: "",
    clientId: id,
    paymentOption: "",
    pmb: "",
    amount: null,
    supervisor: "",
  });

  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    occupation: "",
    dob: "",
    gender: "",
    maritalStatus: "",
    address: "",
    nationality: "",
    nextOfKin: "",
    nextOfKinPhone: "",
    typeOfIdentification: "",
    image: "",
    identification: "",
  });
  const navigate = useNavigate();

  const handleAuthError = (error) => {
    const unauthorizedErrors = [
      "Access Denied: Invalid or expired token. Please login again",
      "Access Denied: Please log in.",
      "Access Denied: Invalid Token.",
      "Access Denied: Invalid or expired token.",
      "Access Denied: Invalid or Expired Token.",
      "Access Denied: No token provided.",
    ];
    if (unauthorizedErrors.includes(error.message)) {
      setMsg("Session Expired!");
      setOpenModalOut(true);
      localStorage.removeItem("royal-token");
    } else {
      console.log(error);
    }
  };

  const loadClient = useCallback(async () => {
    setLoading(true);
    try {
      let token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/client/${id}`;
      const { data } = await apiRequest(url, "GET", null, token);
      setClient(data);
      setLoading(false);
    } catch (error) {
      const unauthorizedErrors = [
        "Access Denied: Invalid or expired token. Please login again",
        "Access Denied: Please log in.",
        "Access Denied: Invalid Token.",
        "Access Denied: Invalid or expired token.",
        "Access Denied: Invalid or Expired Token.",
        "Access Denied: No token provided.",
      ];
      if (unauthorizedErrors.includes(error.message)) {
        setMsg("Session Expired!");
        setOpenModalOut(true);
        localStorage.removeItem("royal-token");
      } else {
        console.log(error);
      }
    }
  }, [id, baseUrl]);

  const loadClientSubs = useCallback(async () => {
    try {
      let token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/client/admin/subscription/${id}`;
      const { data } = await apiRequest(url, "GET", null, token);
      setSubs(Array.isArray(data) ? data.map(hideInactiveSupervisor) : data);
    } catch (error) {
      const unauthorizedErrors = [
        "Access Denied: Invalid or expired token. Please login again",
        "Access Denied: Please log in.",
        "Access Denied: Invalid Token.",
        "Access Denied: Invalid or expired token.",
        "Access Denied: Invalid or Expired Token.",
        "Access Denied: No token provided.",
      ];
      if (unauthorizedErrors.includes(error.message)) {
        setMsg("Session Expired!");
        setOpenModalOut(true);
        localStorage.removeItem("royal-token");
      } else {
        console.log(error);
      }
    }
  }, [id, baseUrl]);

  const loadProperties = useCallback(
    async (a) => {
      setLoading(true);
      try {
        let token = localStorage.getItem("royal-token");
        let url = `${baseUrl}/properties`;
        const { data } = await apiRequest(url, "GET", null, token);
        setProperties(data.properties);
      } catch (error) {
        handleAuthError(error);
      } finally {
        setLoading(false);
      }
    },
    [baseUrl],
  );

  const loadStaff = useCallback(async () => {
    try {
      let token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/staff`;
      const { data } = await apiRequest(url, "GET", null, token);
      setStaff(
        Array.isArray(data)
          ? data.filter((person) => !isInactiveStaff(person))
          : [],
      );
    } catch (error) {
      handleAuthError(error);
    }
  }, [baseUrl]);

  useEffect(() => {
    Promise.all([loadClient(), loadClientSubs(), loadProperties(), loadStaff()])
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, [loadClient, loadClientSubs, loadProperties, loadStaff]);

  const respondToSub = async (action, id) => {
    if (action === "update") {
      const selectedSub = subs?.find((sub) => sub.id === id);
      const selectedSupervisor =
        selectedSub?.supervisor?.id || selectedSub?.supervisor || "";

      setCurrentSubId(id);
      setUpdateSubscription((prev) => ({
        ...prev,
        supervisor: selectedSupervisor,
      }));
      setOpenUpdateModal(true);
      return; // Exit here to wait for modal submission
    }

    const body = {
      approved: action === "approve",
      status: action === "approve" ? "in progress" : "cancelled",
    };

    await confirmDialog(`Are you sure you want to ${action}?`, {
      title:
        action === "approve" ? "Approve Subscription" : "Update Subscription",
      confirmText: action === "approve" ? "Yes, approve" : "Yes, continue",
      color: action === "approve" ? "success" : "warning",
      loadingText: action === "approve" ? "Approving..." : "Updating...",
      onConfirm: async () => {
        await handleApiRequest(body, id);
      },
    });
  };

  const handleApiRequest = async (body, id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/approve/subscription/${id}`;
      const { data } = await apiRequest(url, "PUT", body, token);

      setSubs((prevSubs) =>
        prevSubs.map((sub) => (sub.id === data.id ? data : sub)),
      );

      await loadClientSubs();
    } catch (error) {
      const unauthorizedErrors = [
        "Access Denied: Invalid or expired token. Please login again",
        "Access Denied: Please log in.",
        "Access Denied: Invalid Token.",
        "Access Denied: Invalid or expired token.",
        "Access Denied: Invalid or Expired Token.",
        "Access Denied: No token provided.",
      ];
      if (unauthorizedErrors.includes(error.message)) {
        setMsg("Session Expired!");
        setOpenModalOut(true);
        localStorage.removeItem("royal-token");
      } else {
        console.log(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelSub = async (a) => {
    await confirmDialog("Are you sure you want to cancel this subscription?", {
      title: "Cancel Subscription",
      confirmText: "Yes, cancel",
      color: "failure",
      loadingText: "Cancelling...",
      onConfirm: async () => {
        setLoading(true);
        try {
          let token = localStorage.getItem("royal-token");
          const url = `${baseUrl}/cancel/subscription/${a}`;
          await apiRequest(url, "PUT", null, token);
          loadClientSubs();
        } catch (error) {
          const unauthorizedErrors = [
            "Access Denied: Invalid or expired token. Please login again",
            "Access Denied: Please log in.",
            "Access Denied: Invalid Token.",
            "Access Denied: Invalid or expired token.",
            "Access Denied: Invalid or Expired Token.",
            "Access Denied: No token provided.",
          ];
          if (unauthorizedErrors.includes(error.message)) {
            setMsg("Session Expired!");
            setOpenModalOut(true);
            localStorage.removeItem("royal-token");
          } else {
            console.log(error);
          }
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const approveClientProfile = async () => {
    await confirmDialog(
      "Are you sure you want to approve this client profile?",
      {
        title: "Approve Client Profile",
        confirmText: "Yes, approve",
        color: "success",
        loadingText: "Approving...",
        onConfirm: async () => {
          setLoading(true);
          try {
            let token = localStorage.getItem("royal-token");
            const url = `${baseUrl}/status/client/${id}`;
            await apiRequest(url, "PUT", { approved: true }, token);
            loadClient();
          } catch (error) {
            const unauthorizedErrors = [
              "Access Denied: Invalid or expired token. Please login again",
              "Access Denied: Please log in.",
              "Access Denied: Invalid Token.",
              "Access Denied: Invalid or expired token.",
              "Access Denied: Invalid or Expired Token.",
              "Access Denied: No token provided.",
            ];
            if (unauthorizedErrors.includes(error.message)) {
              setMsg("Session Expired!");
              setOpenModalOut(true);
              localStorage.removeItem("royal-token");
            } else {
              console.log(error);
            }
          } finally {
            setLoading(false);
          }
        },
      },
    );
  };

  const handleUpdateSubscription = async () => {
    if (!updateAction) return;

    setLoadingUpdate(true);

    try {
      const token = localStorage.getItem("royal-token");

      // 🔁 Update property & unit
      if (updateAction === "property-unit") {
        if (!updateSubscription.propertyId || !updateSubscription.unitId) {
          alert("Please select property and unit");
          return;
        }

        await apiRequest(
          `${baseUrl}/subscription/${currentSubId}/update`,
          "PUT",
          {
            propertyId: updateSubscription.propertyId,
            unitId: updateSubscription.unitId,
            amount: updateSubscription.amount,
          },
          token,
        );
      }

      // 💰 Add payment
      if (updateAction === "payment") {
        if (!paymentData.amountPaid) {
          alert("Enter payment amount");
          return;
        }

        await apiRequest(
          `${baseUrl}/subscription/${currentSubId}/pay`,
          "POST",
          {
            amountPaid: Number(paymentData.amountPaid),
            proof: paymentData.proof,
          },
          token,
        );
      }

      // 👤 Add or update supervisor
      if (updateAction === "supervisor") {
        if (!updateSubscription.supervisor) {
          alert("Please select supervisor");
          return;
        }

        await apiRequest(
          `${baseUrl}/subscription/${currentSubId}/update`,
          "PUT",
          {
            supervisor: updateSubscription.supervisor,
          },
          token,
        );
      }

      setOpenUpdateModal(false);
      setUpdateAction("");

      loadClientSubs();
    } catch (err) {
      alert(err, "xxxxxxxx msg");
      console.error(err);
    } finally {
      setLoadingUpdate(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSubscription((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const openClientProfileModal = () => {
    setProfileError("");
    setProfileData({
      firstName: client?.firstName || "",
      lastName: client?.lastName || "",
      phone: client?.phone || "",
      email: client?.email || "",
      occupation: client?.occupation || "",
      dob: client?.dob ? new Date(client.dob).toISOString().split("T")[0] : "",
      gender: client?.gender || "",
      maritalStatus: client?.maritalStatus || "",
      address: client?.address || "",
      nationality: client?.nationality || "",
      nextOfKin: client?.nextOfKin || "",
      nextOfKinPhone: client?.nextOfKinPhone || "",
      typeOfIdentification: client?.typeOfIdentification || "",
      image: client?.image || "",
      identification: client?.identification || "",
    });
    setOpenEditProfileModal(true);
  };

  const updateClientProfile = async () => {
    setLoadingProfileUpdate(true);
    setProfileError("");
    try {
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/client/admin/${id}`;
      await apiRequest(url, "PUT", profileData, token);
      setOpenEditProfileModal(false);
      loadClient();
    } catch (error) {
      const unauthorizedErrors = [
        "Access Denied: Invalid or expired token. Please login again",
        "Access Denied: Please log in.",
        "Access Denied: Invalid Token.",
        "Access Denied: Invalid or expired token.",
        "Access Denied: Invalid or Expired Token.",
        "Access Denied: No token provided.",
      ];
      if (unauthorizedErrors.includes(error.message)) {
        setMsg("Session Expired!");
        setOpenModalOut(true);
        localStorage.removeItem("royal-token");
      } else {
        setProfileError(error.message || "Failed to update client profile");
      }
    } finally {
      setLoadingProfileUpdate(false);
    }
  };

  const handleFileChange = (e) => {
    const { files } = e.target;
    if (files) {
      const selectedImages = Array.from(files).slice(0, 1); // Limit to 4 files
      setFiles(selectedImages); // Update the state with selected files
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setError("");
    setSubscription({
      propertyId: "",
      unitId: "",
      clientId: id,
      paymentOption: "",
      pmb: "",
      amount: null,
      supervisor: "",
    });
  };

  const handleCloseModalImage = () => {
    setOpenModalImage(false);
    setError("");
    setFiles([]);
    setImageOrId("");
    setLoading3(false);
  };

  let logOut = () => {
    navigate("/");
  };

  const subscribeToProperty = async (a, b) => {
    if (
      !subscription.clientId ||
      !subscription.unitId ||
      !subscription.paymentOption ||
      !subscription.amount ||
      !subscription.propertyId ||
      !subscription.supervisor
    ) {
      setErrorColor("red");
      setError("Please fill all missing fields.");
      return;
    }
    await confirmDialog(
      "Are you sure you want to subscribe client to the selected property?",
      {
        title: "Subscribe Client",
        confirmText: "Yes, subscribe",
        color: "success",
        loadingText: "Subscribing...",
        onConfirm: async () => {
          setLoading3(true);
          try {
            let token = localStorage.getItem("royal-token");
            const url = `${baseUrl}/subscription`;
            await apiRequest(url, "POST", subscription, token);
            setErrorColor("green");
            setError("Successfully subscribed.");
            setLoading3(false);
            setSubscription({
              propertyId: "",
              clientId: id,
              paymentOption: "",
              pmb: "",
              amount: null,
              supervisor: "",
            });
            setError("");
            handleCloseModal();
            loadClientSubs();
            setLoading(false);
          } catch (error) {
            const unauthorizedErrors = [
              "Access Denied: Invalid or expired token. Please login again",
              "Access Denied: Please log in.",
              "Access Denied: Invalid Token.",
              "Access Denied: Invalid or expired token.",
              "Access Denied: Invalid or Expired Token.",
              "Access Denied: No token provided.",
            ];
            if (unauthorizedErrors.includes(error.message)) {
              setMsg("Session Expired!");
              setOpenModalOut(true);
              localStorage.removeItem("royal-token");
            } else {
              setErrorColor("red");
              setError(error.message);
              setLoading3(false);
              console.log(error);
            }
          }
        },
      },
    );
  };

  const editImage = async () => {
    if (!files) {
      setErrorColor("red");
      setError(
        `Please select a ${imageOrId === "image" ? "image" : "file"} to upload`,
      );
      return;
    }

    const confirmationMessage =
      imageOrId === "image"
        ? "Are you sure you want to change the client's image?"
        : "Are you sure you want to change the client's identification?";
    await confirmDialog(confirmationMessage, {
      title:
        imageOrId === "image"
          ? "Change Client Image"
          : "Change Client Identification",
      confirmText: "Yes, change",
      color: "warning",
      loadingText: "Changing...",
      onConfirm: async () => {
        setLoading3(true);

        try {
          const token = localStorage.getItem("royal-token");
          const url =
            imageOrId === "image"
              ? `${baseUrl}/image/client/${id}`
              : `${baseUrl}/identity/client/${id}`;

          const formData = new FormData();
          files.forEach((file) => formData.append("files", file));

          await apiRequest(url, "PUT", formData, token);

          setErrorColor("green");
          setError(
            `Successfully changed ${
              imageOrId === "image" ? "image" : "identification"
            }.`,
          );

          setLoading3(false);
          loadClient();
          handleCloseModalImage();
        } catch (error) {
          const unauthorizedErrors = [
            "Access Denied: Invalid or expired token. Please login again",
            "Access Denied: Please log in.",
            "Access Denied: Invalid Token.",
            "Access Denied: Invalid or expired token.",
            "Access Denied: Invalid or Expired Token.",
            "Access Denied: No token provided.",
          ];

          if (unauthorizedErrors.includes(error.message)) {
            setMsg("Session Expired!");
            setOpenModalOut(true);
            localStorage.removeItem("royal-token");
          } else {
            setErrorColor("red");
            setError(error.message);
            console.error(error);
          }
          setLoading3(false);
        }
      },
    });
  };

  const { canWriteSubscriptions, canWriteClients, canDeleteSubscriptions } =
    useAllPermissions();

  return (
    <>
      <title>Clients</title>
      <meta
        name="description"
        content="Customers Page to the Royalmines Property Office"
      />
      <DashboardLayout active="clients" animation="zoom-in">
        <div className="col-span-5 md:col-span-4 bg-white dark:bg-gray-800 rounded-lg shadow-xl m-6">
          {/* Header Area: Breadcrumb and Actions */}
          <div className="flex flex-col sm:flex-row justify-between sm:items-center pb-4 mb-4 border-b border-gray-200 dark:border-gray-700 p-6">
            {/* Title & Breadcrumb */}
            <div className="flex flex-col mb-4 sm:mb-0">
              <Breadcrumb className="mb-2">
                <Link
                  to="/clients"
                  className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white transition-colors"
                >
                  Clients
                </Link>
                <Breadcrumb.Item>
                  <h1 className="text-xl font-bold leading-none text-gray-900 dark:text-white">
                    {client?.firstName} {client?.lastName}
                  </h1>
                </Breadcrumb.Item>
              </Breadcrumb>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              {!client.approved && (
                <Button
                  disabled={
                    !canWriteClients ||
                    loggedInUser?.role?.title.toLowerCase() !== "admin"
                  }
                  color="success"
                  onClick={() => approveClientProfile()}
                  className="min-w-[170px] bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
                >
                  <IoMdAddCircleOutline className="me-2 h-5 w-5" />
                  Approve Client Profile
                </Button>
              )}
              <Button
                disabled={!canWriteClients}
                color="light"
                onClick={openClientProfileModal}
                className="min-w-[170px]"
              >
                <HiPencil className="me-2 h-5 w-5" />
                Edit Client Profile
              </Button>
              {client.approved && (
                <Button
                  disabled={!canWriteSubscriptions}
                  color="success"
                  onClick={() => setOpenModal(true)}
                  className="min-w-[170px] bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
                >
                  <IoMdAddCircleOutline className="me-2 h-5 w-5" />
                  New Subscription
                </Button>
              )}
              <Button
                as={Link}
                to="/clients"
                className="bg-indigo-600 hover:bg-indigo-700 text-white dark:bg-indigo-700 dark:hover:bg-indigo-800"
              >
                <IoMdArrowBack className="me-2 h-5 w-5" />
                Go Back
              </Button>
            </div>
          </div>

          {!loading ? (
            <>
              {/* Tabs Section */}
              <div className="mt-6">
                <Tabs.Group
                  aria-label="Client Details Tabs"
                  id="client-tabs"
                  className="w-full"
                >
                  {/* --- Profile Tab --- */}
                  <Tabs.Item active title="Profile">
                    <Card className="p-4 border-t-2 border-indigo-500 dark:border-indigo-600 uppercase">
                      {/* Profile Summary Section */}
                      <div className="flex flex-col items-center md:flex-row md:items-start md:justify-start gap-8 border-b pb-6 mb-6 border-gray-200 dark:border-gray-700">
                        {/* Image and Edit Icon */}
                        <div className="relative flex-shrink-0">
                          <img
                            src={client.image || img}
                            alt={`${client.firstName} ${client.lastName}`}
                            className={`object-cover h-32 w-32 ${
                              !client.image ? "rounded-full" : "rounded-lg"
                            } border-4 border-indigo-300 dark:border-indigo-500`}
                          />
                          {canWriteClients && (
                            <Tooltip content="Change Client Image">
                              <HiPencil
                                size={20}
                                className="absolute bottom-0 right-0 p-1 bg-white dark:bg-gray-700 rounded-full text-indigo-600 dark:text-indigo-400 shadow cursor-pointer transition-colors hover:text-indigo-800"
                                onClick={() => {
                                  setOpenModalImage(true);
                                  setImageOrId("image");
                                }}
                              />
                            </Tooltip>
                          )}
                        </div>

                        {/* Basic Info (Two Columns) */}
                        <div className="flex-grow grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 w-full">
                          <h2 className="col-span-full text-2xl font-extrabold text-gray-900 dark:text-white mb-2">
                            {client.firstName} {client.lastName}
                          </h2>

                          <p className="text-sm dark:text-gray-400">
                            <strong className="font-semibold text-gray-700 dark:text-gray-200">
                              Phone:
                            </strong>{" "}
                            {client.phone}
                          </p>
                          <p className="text-sm dark:text-gray-400">
                            <strong className="font-semibold text-gray-700 dark:text-gray-200">
                              Email:
                            </strong>{" "}
                            <span className="lowercase">{client.email}</span>
                          </p>
                          <p className="text-sm dark:text-gray-400">
                            <strong className="font-semibold text-gray-700 dark:text-gray-200">
                              Occupation:
                            </strong>{" "}
                            {client.occupation}
                          </p>
                          <p className="text-sm dark:text-gray-400">
                            <strong className="font-semibold text-gray-700 dark:text-gray-200">
                              Date Of Birth:
                            </strong>{" "}
                            {formatDate(client.dob)}
                          </p>
                          <p className="text-sm dark:text-gray-400">
                            <strong className="font-semibold text-gray-700 dark:text-gray-200">
                              Gender:
                            </strong>{" "}
                            {client.gender}
                          </p>
                          <p className="text-sm dark:text-gray-400">
                            <strong className="font-semibold text-gray-700 dark:text-gray-200">
                              Marital Status:
                            </strong>{" "}
                            {client.maritalStatus}
                          </p>
                        </div>
                      </div>

                      {/* Contact & Kin Details Section */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                        {/* Address and Nationality */}
                        <div className="space-y-3">
                          <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-3 border-b border-gray-100 dark:border-gray-700 pb-1">
                            Contact Details
                          </h5>
                          <DetailRow label="Address" value={client?.address} />
                          <DetailRow
                            label="Nationality"
                            value={client.nationality}
                          />
                        </div>

                        {/* Next of Kin Details */}
                        <div className="space-y-3">
                          <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-3 border-b border-gray-100 dark:border-gray-700 pb-1">
                            Next of Kin
                          </h5>
                          <DetailRow label="Name" value={client.nextOfKin} />
                          <DetailRow
                            label="Phone"
                            value={client.nextOfKinPhone}
                          />
                        </div>
                      </div>

                      {/* Identification Document Section */}
                      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <h5 className="font-bold text-xl mb-4 text-center text-gray-900 dark:text-white">
                          Identification ({client.typeOfIdentification})
                        </h5>

                        <div className="flex justify-center items-center">
                          {client.identification ? (
                            <div className="relative w-full max-w-lg shadow-xl rounded-lg overflow-hidden">
                              <img
                                className="object-contain w-full h-auto rounded-lg"
                                src={client.identification}
                                alt={`${client.firstName}'s Identification`}
                              />
                              {canWriteClients && (
                                <Tooltip content="Change Client ID">
                                  <HiPencil
                                    size={24}
                                    className="absolute top-3 right-3 p-1 bg-white dark:bg-gray-700 rounded-full text-red-600 dark:text-red-400 shadow-lg cursor-pointer transition-colors hover:text-red-800"
                                    onClick={() => {
                                      setOpenModalImage(true);
                                      setImageOrId("identification");
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center p-8 bg-gray-50 dark:bg-gray-700 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                              <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                                No identification added yet.
                              </h3>
                              <Button
                                className="mt-4 px-4 py-2 text-white bg-indigo-600 rounded-md hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-800"
                                onClick={() => {
                                  setOpenModalImage(true);
                                  setImageOrId("identification");
                                }}
                                disabled={!canWriteClients}
                              >
                                Upload Client ID
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  </Tabs.Item>

                  {/* --- Subscriptions Tab --- */}
                  {client.approved && (
                    <Tabs.Item title="Subscriptions">
                      <div className="p-4">
                        {subs?.length > 0 ? (
                          <div className="space-y-4">
                            {subs.map((a, b) => (
                              <Card
                                key={b}
                                className="bg-gray-50 dark:bg-gray-700 hover:shadow-lg transition-shadow duration-300"
                              >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  <div className="flex flex-col space-y-2">
                                    <DetailRow
                                      label="Property"
                                      value={a.propertyId.title}
                                    />
                                    <DetailRow
                                      label="Type"
                                      value={a.unitSnapshot?.unit || "N/A"}
                                    />
                                    <DetailRow
                                      label="Address"
                                      value={a.propertyId.address}
                                    />
                                    <DetailRow
                                      label="Sold at"
                                      value={`₦ ${a.amount?.toLocaleString() || "N/A"}`}
                                    />
                                  </div>
                                  <div className="flex flex-col space-y-2">
                                    <DetailRow
                                      label="Payment Option"
                                      value={a.paymentOption}
                                    />
                                    <DetailRow
                                      label="Tx Date"
                                      value={formatDate(a.createdAt)}
                                    />
                                    <DetailRow
                                      label="Value Date"
                                      value={formatDate(a.valueDate) || "N/A"}
                                    />
                                    <DetailRow
                                      label="Status"
                                      value={a.status}
                                    />
                                    <DetailRow
                                      label="Supervisor"
                                      value={
                                        `${a.supervisor?.firstName || ""} ${a.supervisor?.lastName || ""}`.trim() ||
                                        "N/A"
                                      }
                                    />
                                    <Actions
                                      a={a}
                                      handleAction={respondToSub}
                                      handleDelete={cancelSub}
                                      deleteAccess={canDeleteSubscriptions}
                                      writeAccess={canWriteSubscriptions}
                                    />
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        ) : (
                          <h3 className="text-center my-10 text-xl font-medium text-gray-500 dark:text-gray-400">
                            Client has not subscribed to a property yet.
                          </h3>
                        )}
                      </div>
                    </Tabs.Item>
                  )}
                </Tabs.Group>
              </div>
            </>
          ) : (
            <div className="flex justify-center items-center h-64">
              <Spinner
                style={{ color: "#0078e7" }}
                aria-label="info spinner example"
                size="xl"
              />
            </div>
          )}
        </div>
      </DashboardLayout>
      {/* modal for subscription */}
      <Modal
        show={openModal}
        onClose={() => handleCloseModal()}
        theme={{
          root: {
            base: "fixed inset-0 z-[2000] h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
          },
          content: {
            inner:
              "relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col max-h-full",
          },
          backdrop: "fixed inset-0 z-[1990] bg-gray-900/50 dark:bg-gray-900/80",
        }}
      >
        <Modal.Header>Subscribe to Property</Modal.Header>
        <div className="flex justify-center">
          <small
            style={{
              color: errorColor,
              textAlign: "center",
            }}
          >
            {error}
          </small>
        </div>
        <Modal.Body>
          <form>
            <div className="grid grid-cols-1 gap-4">
              {/* Property Select */}
              <Select
                name="propertyId"
                placeholder="Select Property"
                value={subscription.propertyId}
                onChange={(e) => {
                  const propertyId = e.target.value;
                  setSubscription((prev) => ({
                    ...prev,
                    propertyId,
                    unitId: "", // reset unitId when property changes
                  }));
                }}
              >
                <option value="">Select Property</option>
                {properties &&
                  properties?.map((property) => (
                    <option
                      key={property.id}
                      value={property.id}
                      className="uppercase"
                    >
                      {property.title} - {property.propertyType} -{" "}
                      {property.address}
                    </option>
                  ))}
              </Select>

              {/* Unit Select - filtered by selected property */}
              <Select
                name="unitId"
                placeholder="Select Unit"
                value={subscription.unitId}
                onChange={handleInputChange}
                disabled={!subscription.propertyId} // disable if no property selected
              >
                <option value="">Select Unit</option>
                {subscription.propertyId &&
                  properties
                    ?.find((p) => p.id === subscription.propertyId)
                    ?.propertyUnits?.map((unit, index) => (
                      <option key={index} value={unit._id || index}>
                        {unit.unit} - Quantity: {unit.quantity} - Price: ₦
                        {unit.price || "N/A"}
                      </option>
                    ))}
              </Select>

              {/* Payment Option */}
              <Select
                name="paymentOption"
                placeholder="Payment Option"
                value={subscription.paymentOption}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Payment Option</option>
                <option value="one-off">One-Off</option>
                <option value="installment">Installment</option>
                <option value="MREIF">MREIF</option>
                <option value="NHF">NHF</option>
              </Select>

              <Select
                name="supervisor"
                value={subscription.supervisor}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Supervisor</option>
                {staff?.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.firstName} {person.lastName}
                  </option>
                ))}
              </Select>

              {/* PMB */}
              <TextInput
                name="pmb"
                placeholder="Primary Mortgage Bank"
                value={subscription.pmb}
                onChange={handleInputChange}
              />

              <TextInput
                type="number"
                step="0.01"
                name="amount"
                placeholder="Price Sold At"
                min={0}
                value={subscription.amount}
                onChange={handleInputChange}
              />
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={loading3} onClick={() => subscribeToProperty()}>
            {loading3 ? "Loading..." : "Submit"}
          </Button>
          <Button color="gray" onClick={() => handleCloseModal()}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      {/* modal for client image */}
      <Modal
        show={openModalImage}
        onClose={() => handleCloseModalImage()}
        size="md"
        popup
      >
        {/* 🖼️ Header and Title */}
        <Modal.Header className="p-4 border-b dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <span className="text-xl font-bold text-gray-900 dark:text-white">
              {imageOrId === "image"
                ? "Update Client Profile Image"
                : "Update Client Identification Document"}
            </span>
          </div>
        </Modal.Header>

        <Modal.Body className="pt-4 pb-6 px-6">
          {/* ⚠️ Centralized Error Display */}
          {error && (
            <div
              className={`p-3 mb-4 rounded-lg flex items-start text-sm ${
                errorColor === "red"
                  ? "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
                  : "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
              }`}
            >
              <HiOutlineExclamationCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
              <p className="font-medium">{error}</p>
            </div>
          )}

          <form className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              {/* 📂 File Input with Drag-and-Drop Look */}
              <div>
                <div className="mb-2 block">
                  <Label
                    htmlFor="fileUpload"
                    value="Select a New File to Upload *"
                  />
                </div>

                {/* Simulated custom file input area */}
                <label
                  htmlFor="fileUpload"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <HiCloudUpload className="w-8 h-8 mb-3 text-gray-400" />
                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-semibold">Click to upload</span> or
                      drag and drop
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {imageOrId === "image"
                        ? "PNG or JPG (MAX 5MB)"
                        : "Image or PDF file"}
                    </p>

                    {/* Display filename if a file is selected */}
                    {/* Assuming 'selectedFile' state holds the file object */}
                    {/* {selectedFile && (
                                <p className="mt-2 text-sm text-indigo-600 dark:text-indigo-400 font-medium">
                                    File Selected: {selectedFile.name}
                                </p>
                            )} */}
                  </div>
                  <input
                    type="file"
                    id="fileUpload"
                    name="files"
                    accept={
                      imageOrId === "image" ? ".jpg,.png" : ".jpg,.png,.pdf"
                    }
                    onChange={(e) => handleFileChange(e)}
                    className="hidden" // Hide native input, use label as the target
                  />
                </label>
              </div>
            </div>
          </form>
        </Modal.Body>

        {/* 🚀 Footer and Actions */}
        <Modal.Footer className="justify-end p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            color="indigo"
            disabled={loading3}
            onClick={() => editImage()}
          >
            {loading3 ? (
              <>
                {/* Flowbite Spinner or SVG animation */}
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Uploading...
              </>
            ) : (
              <>
                <HiOutlineCheck className="h-5 w-5 mr-2" />
                Save Changes
              </>
            )}
          </Button>
          <Button color="light" onClick={() => handleCloseModalImage()}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      {/* modal for edit client profile */}
      <Modal
        show={openEditProfileModal}
        onClose={() => setOpenEditProfileModal(false)}
        size="2xl"
      >
        <Modal.Header>Edit Client Profile</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            {profileError && (
              <small className="text-red-600 dark:text-red-400">
                {profileError}
              </small>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="profileFirstName" value="First Name" />
                <TextInput
                  id="profileFirstName"
                  name="firstName"
                  placeholder="First Name"
                  value={profileData.firstName}
                  onChange={handleProfileInputChange}
                />
              </div>
              <div>
                <Label htmlFor="profileLastName" value="Last Name" />
                <TextInput
                  id="profileLastName"
                  name="lastName"
                  placeholder="Last Name"
                  value={profileData.lastName}
                  onChange={handleProfileInputChange}
                />
              </div>
              <div>
                <Label htmlFor="profilePhone" value="Phone" />
                <TextInput
                  id="profilePhone"
                  name="phone"
                  placeholder="Phone"
                  value={profileData.phone}
                  onChange={handleProfileInputChange}
                />
              </div>
              <div>
                <Label htmlFor="profileEmail" value="Email" />
                <TextInput
                  id="profileEmail"
                  name="email"
                  placeholder="Email"
                  value={profileData.email}
                  onChange={handleProfileInputChange}
                />
              </div>
              <div>
                <Label htmlFor="profileOccupation" value="Occupation" />
                <TextInput
                  id="profileOccupation"
                  name="occupation"
                  placeholder="Occupation"
                  value={profileData.occupation}
                  onChange={handleProfileInputChange}
                />
              </div>
              <div>
                <Label htmlFor="profileDob" value="Date of Birth" />
                <TextInput
                  id="profileDob"
                  type="date"
                  name="dob"
                  value={profileData.dob}
                  onChange={handleProfileInputChange}
                />
              </div>
              <div>
                <Label htmlFor="profileGender" value="Gender" />
                <Select
                  id="profileGender"
                  name="gender"
                  value={profileData.gender}
                  onChange={handleProfileInputChange}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="profileMaritalStatus" value="Marital Status" />
                <Select
                  id="profileMaritalStatus"
                  name="maritalStatus"
                  value={profileData.maritalStatus}
                  onChange={handleProfileInputChange}
                >
                  <option value="">Select Marital Status</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="profileAddress" value="Address" />
                <TextInput
                  id="profileAddress"
                  name="address"
                  placeholder="Address"
                  value={profileData.address}
                  onChange={handleProfileInputChange}
                />
              </div>
              <div>
                <Label htmlFor="profileNationality" value="Nationality" />
                <TextInput
                  id="profileNationality"
                  name="nationality"
                  placeholder="Nationality"
                  value={profileData.nationality}
                  onChange={handleProfileInputChange}
                />
              </div>
              <div>
                <Label htmlFor="profileNextOfKin" value="Next of Kin" />
                <TextInput
                  id="profileNextOfKin"
                  name="nextOfKin"
                  placeholder="Next of Kin"
                  value={profileData.nextOfKin}
                  onChange={handleProfileInputChange}
                />
              </div>
              <div>
                <Label
                  htmlFor="profileNextOfKinPhone"
                  value="Next of Kin Phone"
                />
                <TextInput
                  id="profileNextOfKinPhone"
                  name="nextOfKinPhone"
                  placeholder="Next of Kin Phone"
                  value={profileData.nextOfKinPhone}
                  onChange={handleProfileInputChange}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="success"
            onClick={updateClientProfile}
            disabled={loadingProfileUpdate}
          >
            {loadingProfileUpdate ? "Updating..." : "Save Changes"}
          </Button>
          <Button color="gray" onClick={() => setOpenEditProfileModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      {/* modal for logout */}
      <Modal show={openModalOut} size="md" popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              {msg}
            </h3>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={() => logOut()}>
                {"OK"}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      {/* modal for update */}
      <Modal
        show={openUpdateModal}
        onClose={() => setOpenUpdateModal(false)}
        size="lg"
        popup
      >
        <Modal.Header>
          <div className="flex items-center space-x-2">
            <HiPencil className="h-6 w-6 text-indigo-600" />
            <span className="text-xl font-bold">Update Subscription</span>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="space-y-6">
            {/* Select update action */}
            <div>
              <Label value="What would you like to update?" />
              <Select
                value={updateAction}
                onChange={(e) => {
                  setUpdateAction(e.target.value);
                  setUpdateSubscription({
                    propertyId: "",
                    unitId: "",
                    amount: null,
                    supervisor: "",
                  });
                  setPaymentData({ amountPaid: "", proof: "" });
                }}
              >
                <option value="">-- Select Action --</option>
                <option value="property-unit">Change Property / Unit</option>
                <option value="payment">Add Payment</option>
                <option value="supervisor">Add Supervisor</option>
              </Select>
            </div>

            {/* 🔁 UPDATE PROPERTY + UNIT */}
            {updateAction === "property-unit" && (
              <div className="grid grid-cols-1 gap-4">
                <Select
                  value={updateSubscription.propertyId}
                  onChange={(e) => {
                    const propertyId = e.target.value;
                    setUpdateSubscription((prev) => ({
                      ...prev,
                      propertyId,
                      unitId: "",
                      amount: null,
                    }));
                  }}
                >
                  <option value="">Select Property</option>
                  {properties?.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.title} - {property.address}
                    </option>
                  ))}
                </Select>

                <Select
                  value={updateSubscription.unitId}
                  disabled={!updateSubscription.propertyId}
                  onChange={(e) => {
                    const unitId = e.target.value;

                    const selectedProperty = properties?.find(
                      (p) => p.id === updateSubscription.propertyId,
                    );

                    const selectedUnit = selectedProperty?.propertyUnits?.find(
                      (u) => u._id === unitId,
                    );

                    setUpdateSubscription((prev) => ({
                      ...prev,
                      unitId,
                      amount: selectedUnit?.price || 0,
                    }));
                  }}
                >
                  <option value="">Select Unit</option>

                  {updateSubscription.propertyId &&
                    properties
                      ?.find((p) => p.id === updateSubscription.propertyId)
                      ?.propertyUnits?.map((unit) => (
                        <option key={unit._id} value={unit._id}>
                          {unit.unit} - ₦{unit.price?.toLocaleString()}
                        </option>
                      ))}
                </Select>

                <TextInput
                  type="number"
                  step="0.01"
                  name="amount"
                  placeholder="Price Sold At"
                  disabled={!updateSubscription.unitId}
                  min={0}
                  value={updateSubscription.amount}
                  onChange={(e) =>
                    setUpdateSubscription((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }))
                  }
                />
              </div>
            )}

            {/* 💰 ADD PAYMENT */}
            {updateAction === "payment" && (
              <div className="grid grid-cols-1 gap-4">
                <TextInput
                  type="number"
                  step="0.01"
                  min={0}
                  placeholder="Amount Paid"
                  value={paymentData.amountPaid}
                  onChange={(e) =>
                    setPaymentData((prev) => ({
                      ...prev,
                      amountPaid: e.target.value,
                    }))
                  }
                />

                <TextInput
                  type="text"
                  placeholder="Payment Proof URL (optional)"
                  value={paymentData.proof}
                  onChange={(e) =>
                    setPaymentData((prev) => ({
                      ...prev,
                      proof: e.target.value,
                    }))
                  }
                />
              </div>
            )}

            {/* 👤 ADD SUPERVISOR */}
            {updateAction === "supervisor" && (
              <div className="grid grid-cols-1 gap-4">
                <Select
                  value={updateSubscription.supervisor}
                  onChange={(e) =>
                    setUpdateSubscription((prev) => ({
                      ...prev,
                      supervisor: e.target.value,
                    }))
                  }
                >
                  <option value="">Select Supervisor</option>
                  {staff?.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.firstName} {person.lastName}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            color="success"
            disabled={loadingUpdate || !updateAction}
            onClick={handleUpdateSubscription}
          >
            {loadingUpdate ? "Updating..." : "Submit"}
          </Button>

          <Button
            color="gray"
            onClick={() => {
              setOpenUpdateModal(false);
              setUpdateAction("");
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default SingleCustomer;

const DetailRow = ({ label, value }) => (
  <div className="flex items-center mb-2">
    <p className="text-sm font-medium text-gray-900 dark:text-white mr-2">
      {label}:
    </p>
    <p className="uppercase text-sm text-gray-500 dark:text-gray-400">
      {value}
    </p>
  </div>
);

const Actions = ({
  a,
  handleAction,
  handleDelete,
  deleteAccess = false,
  writeAccess = false,
}) => (
  <div className="flex justify-end gap-3">
    {/* 1. Delete Button 
      Visible if NOT approved. Disabled if deleteAccess is false or already approved.
      'disabled:bg-gray-400 disabled:cursor-not-allowed' handles the disabled styling.
    */}
    {/* {!a.approved && (
      <button
        className="bg-red-500 text-white px-3 py-1 rounded transition-colors
                   hover:bg-red-700 
                   focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500
                   disabled:bg-gray-400 disabled:cursor-not-allowed"
        disabled={!deleteAccess}
        onClick={() => handleDelete(a.id)}
      >
        Cancel
      </button>
    )} */}

    {/* 2. Cancel Button 
      Visible if NOT approved AND status is NOT cancelled.
      Disabled if writeAccess is false.
    */}
    {
      <button
        className="bg-yellow-500 text-white px-3 py-1 rounded transition-colors
                   hover:bg-yellow-700 
                   focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500
                   disabled:bg-gray-400 disabled:cursor-not-allowed"
        onClick={() => handleAction("cancel", a.id)}
        disabled={!writeAccess || a.status === "cancelled"}
      >
        Cancel
      </button>
    }

    {/* 3. Approve Button 
      Visible if NOT approved AND status is NOT cancelled.
      Disabled if writeAccess is false.
    */}
    {a.approved === false && a.status !== "cancelled" && (
      <button
        className="bg-green-500 text-white px-3 py-1 rounded transition-colors
                   hover:bg-green-700 
                   focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500
                   disabled:bg-gray-400 disabled:cursor-not-allowed"
        onClick={() => handleAction("approve", a.id)}
        disabled={!writeAccess}
      >
        Approve
      </button>
    )}

    {/* 4. Update Button 
      Always visible. Disabled if writeAccess is false.
    */}
    <button
      className="bg-blue-500 text-white px-3 py-1 rounded transition-colors
                 hover:bg-blue-700 
                 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                 disabled:bg-gray-400 disabled:cursor-not-allowed"
      onClick={() => handleAction("update", a.id)}
      disabled={!writeAccess || a.status === "cancelled"}
    >
      Update
    </button>
  </div>
);
